/**
 * Estados de cuenta CENACE — cliente portado del Portal (22-sep-2026).
 * Módulo aparte para no chocar con los tipos de api.ts.
 */
import { fetchAuth, jsonFetch } from "./api";

export type EdoctaListItem = {
  clv_sistema: string;
  clv_subcuenta: string;
  fecha_oper: string;
  fecha_emis: string | null;
  file_name: string | null;
  cargos: string;
  creditos: string;
  total_neto_dia: string;
  descargado_en: string | null;
};

export type EdoctaListResponse = {
  items: EdoctaListItem[];
  total: number;
  sum_cargos: string;
  sum_creditos: string;
  sum_total_neto: string;
};

export type Catalogos = {
  sistemas: string[];
  subcuentas: { clv_subcuenta: string; clv_sistema: string }[];
  fecha_min: string | null;
  fecha_max: string | null;
};

export type EdoctaFilters = {
  sistema?: string;
  subcuenta?: string;
  desde?: string;
  hasta?: string;
  sort?: string;
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
};


export function listEdoctas(filters: EdoctaFilters): Promise<EdoctaListResponse> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== "") params.set(k, String(v));
  });
  const qs = params.toString();
  return jsonFetch<EdoctaListResponse>(`/api/etrm/edoctas${qs ? `?${qs}` : ""}`);
}

export function getCatalogos(): Promise<Catalogos> {
  return jsonFetch<Catalogos>("/api/etrm/edoctas/catalogos");
}

export function downloadUrl(subcuenta: string, fecha: string, kind: "pdf" | "xml"): string {
  return `/api/etrm/edoctas/${subcuenta}/${fecha}/download/${kind}`;
}

/**
 * Descarga un PDF/XML usando fetch autenticado y triggerea save dialog.
 * Soporta tanto 302 → signed URL (GCS) como streaming directo del backend.
 */
export async function downloadEdocta(
  subcuenta: string,
  fecha: string,
  kind: "pdf" | "xml",
): Promise<void> {
  const url = downloadUrl(subcuenta, fecha, kind);
  const res = await fetchAuth(url);
  if (!res.ok) {
    throw new Error(`Descarga falló: ${res.status} ${res.statusText}`);
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;

  // Intentar usar el filename del Content-Disposition; si no, construir uno.
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  a.download = match ? match[1] : `${subcuenta}-${fecha}.${kind}`;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

/** Descarga un ZIP con el PDF y XML de los estados de cuenta seleccionados. */
export async function downloadEdoctasZip(
  items: { subcuenta: string; fecha: string }[],
): Promise<void> {
  const res = await fetchAuth("/api/etrm/edoctas/zip", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    throw new Error(`Descarga falló: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  a.download = match ? match[1] : "edoctas.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Detalle ----------------------------------------------------------

export type Concepto = {
  ful: string;
  grupo: string | null;
  descripcion: string | null;
  monto_total: string | null;
  iva: string | null;
  total_neto: string | null;
};

export type Factura = {
  fuf: string;
  num_liq: number;
  tipo: string | null;
  emisor: string | null;
  rfc: string | null;
  razon_social: string | null;
  fecha_pago: string | null;
  ref_bancaria: string | null;
  conceptos: Concepto[];
};

export type Cabecera = {
  fuecd: string;
  clv_participante: string;
  clv_sistema: string;
  clv_subcuenta: string;
  fecha_oper: string;
  fecha_emis: string | null;
  file_name: string | null;
  descargado_en: string | null;
  cargos: string;
  creditos: string;
  total_neto_dia: string;
};

export type EdoctaDetalle = {
  cabecera: Cabecera;
  facturas: Factura[];
};

export function getEdoctaDetalle(
  subcuenta: string,
  fecha: string,
): Promise<EdoctaDetalle> {
  return jsonFetch<EdoctaDetalle>(`/api/etrm/edoctas/${subcuenta}/${fecha}`);
}

// ---------- Telemetría horaria ----------------------------------------------

export type RegistroHorario = {
  hora: number;
  elemento: string;
  nodo: string | null;
  potencia: string | null;
  potencia_mda: string | null;
  potencia_mtr: string | null;
  precio: string | null;
  monto_horario: string | null;
};

export type TelemetriaHoraria = {
  ful: string;
  elementos: string[];
  registros: RegistroHorario[];
};

export function getTelemetriaHoraria(ful: string): Promise<TelemetriaHoraria> {
  return jsonFetch<TelemetriaHoraria>(`/api/etrm/conceptos/${ful}/horario`);
}

// ---------- PML / PND -------------------------------------------------------
